import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cur } from '@/lib/currency';
import api from '@/lib/api';

const fmtSR = (v) => `${cur()} ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// iter817 — Reusable duplicate-expense scanner. Auto-checks the given period on
// mount and shows a red badge when double entries exist (same day + amount +
// branch, even across categories). Opens a review/delete dialog. Used on the
// Expenses page (current month) and drop-in anywhere else.
export function DuplicateExpensesScanner({ startDate, endDate, branchId, periodLabel, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const scan = useCallback(async (opts = {}) => {
    setLoading(true);
    if (opts.openAfter) setOpen(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (branchId) params.branch_id = branchId;
      const r = await api.get('/expenses/find-duplicates', { params });
      setData(r.data);
    } catch { if (opts.openAfter) toast.error('Failed to scan for duplicates'); }
    setLoading(false);
  }, [startDate, endDate, branchId]);

  useEffect(() => { scan(); }, [scan]);

  const deleteRow = async (id) => {
    try {
      const _r = await api.delete(`/expenses/${id}`);
      toast.success(_r.data?.message || 'Duplicate expense deleted');
      await scan();
      onDeleted && onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const count = data?.group_count || 0;

  return (
    <>
      <button
        onClick={() => scan({ openAfter: true })}
        data-testid="expenses-duplicates-btn"
        title="Scan this period for duplicate expenses (same day + amount + branch)"
        className={`relative flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border ${count > 0 ? 'text-amber-700 bg-amber-50 border-amber-300 hover:bg-amber-100' : 'text-muted-foreground bg-white border-border hover:bg-muted/50'}`}
      >
        <AlertTriangle size={14} /> Duplicates
        {count > 0 && (
          <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] font-bold flex items-center justify-center" data-testid="expenses-duplicates-badge">
            {count}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80dvh] overflow-y-auto" data-testid="expenses-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" /> Duplicate Expenses{periodLabel ? ` — ${periodLabel}` : ''}</DialogTitle>
            <DialogDescription>
              Expenses with the same day, amount and branch (even under different categories) — a likely double entry that inflates your reports. Review and delete the extra one.
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Scanning…</div>
          ) : !data || data.group_count === 0 ? (
            <div className="py-10 text-center text-sm text-emerald-600 font-medium" data-testid="expenses-duplicates-none">No duplicate expenses found in this period. ✓</div>
          ) : (
            <div className="space-y-3" data-testid="expenses-duplicates-list">
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Found <b>{data.group_count}</b> duplicate cluster{data.group_count > 1 ? 's' : ''} · <b>{data.total_extra_rows}</b> extra row{data.total_extra_rows > 1 ? 's' : ''} inflating expenses by <b>{fmtSR(data.total_inflated_amount)}</b>.
              </div>
              {data.duplicate_groups.map((g, gi) => (
                <div key={gi} className="border border-border rounded-lg p-3" data-testid={`exp-dup-group-${gi}`}>
                  <div className="text-xs font-semibold mb-2">{g.date} · {fmtSR(g.amount)} · {g.branch_name} · {g.count}× ({g.distinct_categories.join(', ')})</div>
                  <div className="space-y-1.5">
                    {g.rows.map((r, ri) => (
                      <div key={r.id} className="flex items-center gap-2 text-xs border-t border-border/40 pt-1.5" data-testid={`exp-dup-row-${r.id}`}>
                        <span className="font-medium">{r.category}</span>
                        <span className="text-muted-foreground truncate max-w-[220px]">{r.description || r.supplier_name || r.employee_name || '—'}</span>
                        <span className="text-muted-foreground">{r.payment_mode}</span>
                        {ri === 0
                          ? <span className="ml-auto text-[10px] text-emerald-600 font-medium">keep</span>
                          : <Button size="sm" variant="outline" className="ml-auto h-6 text-[10px] px-2 text-error border-error/40 hover:bg-error/10" onClick={() => deleteRow(r.id)} data-testid={`exp-dup-delete-${r.id}`}><Trash2 size={12} className="mr-1" />Delete</Button>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
